// web/src/components/AcceptStripe.tsx — "Set up payments": embedded Stripe onboarding (portal spec 16).
// Mobile-first: w-full, no fixed widths; the embed follows the dashboard theme and must fit 390px (00 §9).
// States: not-connected → onboard → "ready ✓". NO fee shown to the client as our income (CONTRACT).
// The Connect.js instance is created lazily (only on "Set up payments", only with a real publishable
// key) so the component is safe under VITE_USE_MOCK / when the key is absent.
import { useEffect, useState, useCallback } from 'react';
import { ConnectComponentsProvider, ConnectAccountOnboarding } from '@stripe/react-connect-js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, IS_MOCK } from '../lib/api';
import { STRIPE_PK, connectInstance as startConnect, type ConnectInstance } from '../lib/stripeConnect';
import { Icon } from './Icon';

export function AcceptStripe() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [connectInstance, setConnectInstance] = useState<ConnectInstance | null>(null);

  // Status drives the badge + the go-live gate; poll briefly while onboarding is open
  // (charges_enabled may flip seconds after the embed completes, before the webhook lands).
  const status = useQuery({
    queryKey: ['connectStatus'],
    queryFn: () => api.connectStatus(),
    refetchInterval: (q) => (open && !q.state.data?.chargesEnabled ? 4000 : false),
  });

  const startOnboarding = useCallback(() => {
    setOpen(true);
    // Only initialize the real embed when we have a publishable key (skipped in mock).
    if (STRIPE_PK && !IS_MOCK && !connectInstance) {
      setConnectInstance(startConnect());
    } else if (IS_MOCK) {
      // Mock: kick the session so the stateful mock flips charges_enabled. (Real mode without a key: no account is made.)
      void api.connectSession().then(() => qc.invalidateQueries({ queryKey: ['connectStatus'] }));
    }
  }, [connectInstance, qc]);

  const s = status.data;
  const chargesEnabled = !!s?.chargesEnabled;

  useEffect(() => { if (chargesEnabled) setOpen(false); }, [chargesEnabled]);

  return (
    <section className="w-full rounded-lg border border-hair bg-surface p-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-head text-ink">
          <Icon name="wallet" size={18} className={chargesEnabled ? 'text-run' : 'text-warn'} />
          Set up payments
        </h2>
        {chargesEnabled && (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-run px-3 py-1 text-xs text-run">
            <Icon name="check" size={14} /> Ready
          </span>
        )}
      </div>

      {chargesEnabled ? (
        <p className="mt-2 text-sm text-muted">
          You're set up to take payments. Money from orders goes straight to your Stripe account.
        </p>
      ) : !open ? (
        <>
          <p className="mt-2 text-sm text-muted">
            {s?.connected
              ? 'You started setup but it isn’t finished. A few more details and your website can take orders.'
              : 'Free Stripe account through us. Stripe’s normal card fee comes out of each sale, like any card processor. Your customers pay a 25¢ online service fee per order; you pay us nothing extra.'}
          </p>
          <button
            onClick={startOnboarding}
            className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-md bg-run px-4 font-medium text-runink transition-transform active:scale-[0.98]"
          >
            <Icon name="rocket" size={16} />
            {s?.connected ? 'Finish setup' : 'Set up payments'}
          </button>
        </>
      ) : connectInstance ? (
        // Embedded onboarding — w-full, no fixed width → fits 390px (00 §9).
        <div className="mt-4 w-full">
          <ConnectComponentsProvider connectInstance={connectInstance}>
            <ConnectAccountOnboarding
              onExit={() => {
                setOpen(false);
                qc.invalidateQueries({ queryKey: ['connectStatus'] });
              }}
            />
          </ConnectComponentsProvider>
        </div>
      ) : (
        <p className="mt-4 text-sm text-muted">
          {STRIPE_PK || IS_MOCK ? 'Opening secure Stripe setup…' : 'Payments setup isn’t switched on yet. Send us a message and we’ll set it up with you.'}
        </p>
      )}
    </section>
  );
}
